import React, { useState, useEffect } from 'react';
import { Search, Eye, Calendar, User } from 'lucide-react';
import { apiService } from '../../services/api';

interface Paciente {
  _id: string;
  usuario: {
    nome: string;
    email: string;
  };
}

interface Anamnese {
  _id: string;
  createdAt: string;
  anamnese: {
    historicoFamiliar: string;
    habitosAlimentares: string;
    atividadeFisica: string;
    sono: string;
    stress: string;
    objetivos: string;
    medicamentos: string;
    alergias: string;
    observacoes: string;
  };
}

const ConsultarAnamnese: React.FC = () => {
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState<string>('');
  const [anamneses, setAnamneses] = useState<Anamnese[]>([]);
  const [anamneseSelecionada, setAnamneseSelecionada] = useState<Anamnese | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    carregarPacientes();
  }, []);

  const carregarPacientes = async () => {
    try {
      const data = await apiService.getPacientes();
      setPacientes(data);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
      // Mock data para desenvolvimento
      setPacientes([
        {
          _id: '1',
          usuario: { nome: 'Maria Silva', email: 'maria@email.com' }
        },
        {
          _id: '2', 
          usuario: { nome: 'João Santos', email: 'joao@email.com' }
        }
      ]);
    }
  };

  const carregarAnamneses = async (pacienteId: string) => {
    try {
      setLoading(true);
      const data = await apiService.getAvaliacoes(pacienteId, 'anamnese');
      setAnamneses(data);
    } catch (error) {
      console.error('Erro ao carregar anamneses:', error);
      // Mock data
      setAnamneses([
        {
          _id: '1',
          createdAt: '2024-10-15T10:00:00Z',
          anamnese: {
            historicoFamiliar: 'Diabetes na família',
            habitosAlimentares: 'Come muito doce, pula refeições',
            atividadeFisica: 'Sedentária',
            sono: '6-7 horas por noite',
            stress: 'Alto nível de stress no trabalho',
            objetivos: 'Perder 10kg e controlar glicemia',
            medicamentos: 'Metformina 500mg',
            alergias: 'Lactose',
            observacoes: 'Paciente motivada para mudanças'
          }
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const renderAnamnesesContent = () => {
    if (loading) {
      return (
        <div className="text-center py-3">
          <output className="spinner-border spinner-border-sm text-success">
            <span className="visually-hidden">Carregando...</span>
          </output>
        </div>
      );
    }

    if (anamneses.length === 0) {
      return (
        <div className="text-center text-muted py-3">
          <Search size={32} className="mb-2" />
          <p>Nenhuma anamnese encontrada</p>
        </div>
      );
    }

    return (
      <div className="list-group">
        {anamneses.map(anamnese => (
          <button
            key={anamnese._id}
            className={`list-group-item list-group-item-action ${anamneseSelecionada?._id === anamnese._id ? 'active' : ''}`}
            onClick={() => visualizarAnamnese(anamnese)}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-1">
                  <Calendar size={16} className="me-2" />
                  {new Date(anamnese.createdAt).toLocaleDateString('pt-BR')}
                </h6>
                <p className="mb-1 small text-truncate">
                  {anamnese.anamnese.objetivos || 'Sem objetivos definidos'}
                </p>
              </div>
              <Eye size={16} />
            </div>
          </button>
        ))}
      </div>
    );
  };

  const handlePacienteChange = (pacienteId: string) => {
    setPacienteSelecionado(pacienteId);
    setAnamneseSelecionada(null);
    if (pacienteId) {
      carregarAnamneses(pacienteId);
    } else {
      setAnamneses([]);
    }
  };

  const visualizarAnamnese = (anamnese: Anamnese) => {
    setAnamneseSelecionada(anamnese);
  };

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-lg-4">
          <div className="card shadow-sm">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">📝 Consultar Anamneses</h5>
            </div>
            <div className="card-body">
              {/* Seleção de Paciente */}
              <div className="mb-3">
                <label htmlFor="patient-select-anamnese" className="form-label">Selecionar Paciente</label>
                <select 
                  id="patient-select-anamnese"
                  className="form-select"
                  value={pacienteSelecionado}
                  onChange={(e) => handlePacienteChange(e.target.value)}
                >
                  <option value="">Escolha um paciente...</option>
                  {pacientes.map(paciente => (
                    <option key={paciente._id} value={paciente._id}>
                      {paciente.usuario.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lista de Anamneses */}
              {pacienteSelecionado && (
                <div>
                  <h6 className="mb-3">Anamneses Registradas</h6>
                  {renderAnamnesesContent()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          {anamneseSelecionada ? (
            <div className="card shadow-sm">
              <div className="card-header bg-light">
                <div className="d-flex align-items-center">
                  <User size={20} className="me-2 text-success" />
                  <div>
                    <h5 className="mb-0">
                      {pacientes.find(p => p._id === pacienteSelecionado)?.usuario.nome}
                    </h5>
                    <small className="text-muted">
                      Anamnese de {new Date(anamneseSelecionada.createdAt).toLocaleDateString('pt-BR')}
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <h6 className="text-success">Histórico Familiar</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.historicoFamiliar || 'Não informado'}
                    </p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <h6 className="text-success">Hábitos Alimentares</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.habitosAlimentares || 'Não informado'}
                    </p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <h6 className="text-success">Atividade Física</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.atividadeFisica || 'Não informado'}
                    </p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <h6 className="text-success">Qualidade do Sono</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.sono || 'Não informado'}
                    </p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <h6 className="text-success">Nível de Stress</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.stress || 'Não informado'}
                    </p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <h6 className="text-success">Medicamentos</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.medicamentos || 'Não informado'}
                    </p>
                  </div>
                  <div className="col-12 mb-3">
                    <h6 className="text-success">Alergias e Intolerâncias</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.alergias || 'Não informado'}
                    </p>
                  </div>
                  <div className="col-12 mb-3">
                    <h6 className="text-success">Objetivos</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.objetivos || 'Não informado'}
                    </p>
                  </div>
                  <div className="col-12 mb-3">
                    <h6 className="text-success">Observações</h6>
                    <p className="text-muted">
                      {anamneseSelecionada.anamnese.observacoes || 'Não informado'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <Search size={64} className="text-muted mb-3" />
                <h5 className="text-muted">Selecione um paciente e uma anamnese</h5>
                <p className="text-muted">Escolha um paciente na lista ao lado para visualizar suas anamneses</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultarAnamnese;